import {Button, Form, Modal, Radio} from 'antd';
import {t} from 'i18next';
import {ExportOutlined} from '@ant-design/icons';
import {useExportData, useModalState} from '@/hooks';
import {RULES} from '@/constants';
import DatePickerComp from '../date-picker';
import {FormatDateEnum} from '@/models';
import styles from './index.module.scss';

export interface IExportProps {
  url: string;
  withDate: boolean;
  btnTitle: string;
  className?: string;
  withModal?: boolean;
  data?: string;
  from?: string;
  to?: string;
  role?: string;
  roleType?: string;
  customTitle?: string;
  isShowRadioBtns?: boolean;
  isAfterTodayValidation?: boolean;
  isSelectFutureStartDate?: boolean;
}

const Export = ({
                  from = 'from',
                  role,
                  roleType,
                  to = 'to',
                  url,
                  data = 'isPrivate',
                  withDate,
                  className = '',
                  btnTitle = t('exportData'),
                  withModal = true,
                  customTitle,
                  isShowRadioBtns = true,
                  isAfterTodayValidation = true,
                  isSelectFutureStartDate = false,
                }: IExportProps) => {
  const [form] = Form.useForm();
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);
  const {isOpen, openModal, closeModal} = useModalState();
  const {isLoading, sendExportRequest} = useExportData();
  const handleSubmit = async () => {
    const isValid = await form.validateFields();
    if (isValid) {
      await sendExportRequest({
        withDate,
        startDate,
        endDate,
        url,
        data,
        roleType,
        from,
        role,
        to,
        withContactData: form.getFieldValue('withContactData'),
        onSuccess: closeModal,
      });
    }
  };

  return (
    <div className={`${styles.exportBtnWrap} ${className}`}>
      <Button
        loading={!withModal && isLoading}
        onClick={
          !withModal
            ? async () => {
              await sendExportRequest({
                withDate: false,
                url,
                from,
                to,
                withContactData: false,
                onSuccess: closeModal,
              });
            }
            : openModal
        }
        type="primary"
        icon={<ExportOutlined/>}>
        {btnTitle}
      </Button>
      {isOpen && withModal && (
        <Modal
          onCancel={closeModal}
          destroyOnClose
          open
          title={customTitle || 'Do You need to send contact information?'}
          footer={[
            <Button key="back" onClick={closeModal}>
              {t('cancel')}
            </Button>,
            <Button key="submit" type="primary" onClick={handleSubmit} loading={isLoading}>
              {t('send')}
            </Button>,
          ]}>
          <Form layout="vertical" form={form} onFinish={handleSubmit}>
            {withDate && (
              <>
                <DatePickerComp
                  placeholder={t('startExportDate')}
                  format={FormatDateEnum.YMD}
                  form={form}
                  label={`${t('startExportDate')} :`}
                  name="startDate"
                  rules={
                    isSelectFutureStartDate
                      ? [
                        RULES.required,
                        {
                          validator: RULES.afterDateValidator(endDate),
                        },
                      ]
                      : [
                        RULES.required,
                        {
                          validator: RULES.afterTodayValidator(t('youCantSelectDateAfterToday')),
                        },
                        {
                          validator: RULES.afterDateValidator(endDate),
                        },
                      ]
                  }
                />
                <DatePickerComp
                  placeholder={t('selectStartDate')}
                  format={FormatDateEnum.YMD}
                  form={form}
                  label={`${t('endExportDate')} :`}
                  name="endDate"
                  rules={
                    isAfterTodayValidation
                      ? [
                        RULES.required,
                        {
                          validator: RULES.afterTodayValidator(t('youCantSelectDateAfterToday')),
                        },
                        {
                          validator: RULES.afterDateValidator(startDate),
                        },
                      ]
                      : [RULES.required]
                  }
                />
              </>
            )}
            {isShowRadioBtns ? (
              <Form.Item name="withContactData" initialValue={false}>
                <Radio.Group>
                  <Radio value>{t('yes')}</Radio>
                  <Radio value={false}>{t('no')}</Radio>
                </Radio.Group>
              </Form.Item>
            ) : (
              <></>
            )}
          </Form>
        </Modal>
      )}
    </div>
  );
};
export default Export;
